import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';
import { SidebarComponent } from '../../../shared/components/sidebar/sidebar.component';
import { ClientInfoBarComponent } from '../../../shared/components/client-info-bar/client-info-bar.component';
import { PageSkeletonComponent } from '../../../shared/components/page-skeleton/page-skeleton.component';
import { OrderService } from '../../../core/services/order.service';
import { ShopService } from '../../../core/services/shop.service';
import { NavigationBusyService } from '../../../core/services/navigation-busy.service';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [RouterOutlet, SidebarComponent, ClientInfoBarComponent, PageSkeletonComponent],
  templateUrl: './main-layout.component.html',
  styles: [`
    @use '../../../../theme/design-tokens' as *;

    // #main-content becomes the positioning context for the skeleton overlay
    // below: the outlet stays mounted with the outgoing page inside it during a
    // navigation, so the skeleton must sit on top of it rather than in normal
    // flow beside it — see the comment in main-layout.component.html.
    //
    // display: flow-root gives #main-content its own block formatting context.
    // <nav> (the sidebar) is a float: left sibling with no clearing, so per
    // CSS 9.5 a plain block box here would NOT avoid overlapping it — only a
    // box that establishes its own BFC does. Without this, #main-content's
    // box (and therefore the absolutely-positioned skeleton's containing
    // block) would span the sidebar's reserved width too, and since
    // positioned elements paint after floats, the skeleton would render (and
    // intercept clicks) on top of the sidebar during every navigation.
    #main-content {
      position: relative;
      display: flow-root;
    }

    app-page-skeleton {
      position: absolute;
      inset: 0;
      z-index: 1;
      // Matches the panel background master-detail-layout and app-page-skeleton
      // itself already use — not a new token.
      background: $color-bg-primary;
      // A loading placeholder is never itself a click target — belt-and-braces
      // alongside the flow-root fix above, not a substitute for it.
      pointer-events: none;
    }
  `]
})
export class MainLayoutComponent implements OnInit {
  @ViewChild('sidebar') sidebar!: SidebarComponent;
  private orderService = inject(OrderService);
  private shopService = inject(ShopService);
  private router = inject(Router);
  private navigationBusy = inject(NavigationBusyService);

  readonly navigating = this.navigationBusy.busy;
  readonly archetype = this.navigationBusy.archetype;
  readonly listPanel = this.navigationBusy.listPanel;

  ngOnInit() {
    this.shopService.getSites().subscribe(sites => {
      if (sites && sites.length > 0) {
        this.orderService.initializeRealTimeUpdates(sites[0].uniq);
      }
    });

    // Close sidebar on navigation for mobile
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.sidebar && window.innerWidth < 1024) {
          this.sidebar.sidebarVisible = false;
        }
      });
  }
}
